/**
 * AML Compliance Service — Qualitas / Ley de Lavado questionnaire + disclaimer.
 *
 * - Prefills identity from profile + approved KYC so users never retype
 * - Upserts a single aml_declarations row per profile
 * - On submit: express cross_border_transfer consent (written first — Art. 44
 *   D.L. 144/2024 makes it the precondition, not the receipt), then
 *   under_review + legal_acceptances + third_party_sharing consent
 */
import { supabase } from '../supabase'
import { identityService } from './identity.service'
import { ServiceError } from '../errors/service-error'
import { complianceService } from './compliance.service'
import {
  AML_CROSS_BORDER_CONSENT_URL,
  AML_CROSS_BORDER_CONSENT_VERSION,
  QUALITAS_AML_DISCLAIMER_DOCUMENT_TYPE,
  QUALITAS_AML_DISCLAIMER_URL,
  QUALITAS_AML_DISCLAIMER_VERSION,
  QUALITAS_AML_PARTNER,
  AML_SV_ISO2,
} from '../constants/aml'
import { amlFormSchema, emptyAmlFormValues } from '../validations/aml.schema'
import { toAmlEconomicActivity } from '../types/aml.types'
import type {
  AmlDeclaration,
  AmlDeclarationStatus,
  AmlFormValues,
  AmlGender,
  AmlIdentityPrefill,
  AmlIncomeRange,
  AmlFundOrigin,
} from '../types/aml.types'
import type { UserProfile } from '../types/auth.types'
import type { InsertRow, Row } from '../types/db-rows'
import { jsonString, jsonField } from '../types/db-rows'

type AmlRow = Row<'aml_declarations'>
type AmlInsert = InsertRow<'aml_declarations'>

function mapRow(row: AmlRow): AmlDeclaration {
  return {
    id: row.id,
    profileId: row.profile_id,
    fullName: row.full_name,
    phone: row.phone,
    dateOfBirth: row.date_of_birth,
    gender: row.gender as AmlGender,
    nationalityCountryId: row.nationality_country_id,
    originCountryId: row.origin_country_id,
    residenceCountryId: row.residence_country_id,
    incomeRange: row.income_range as AmlIncomeRange,
    economicActivity: row.economic_activity,
    fundOrigin: row.fund_origin as AmlFundOrigin,
    isPep: row.is_pep,
    isPoliticalPartyMember: row.is_political_party_member,
    hasPepFamily: row.has_pep_family,
    hasPepAssociate: row.has_pep_associate,
    hasTaxHavenRelations: row.has_tax_haven_relations,
    hasIllicitOrJudicialHistory: row.has_illicit_or_judicial_history,
    disclaimerVersion: row.disclaimer_version,
    disclaimerAcceptedAt: row.disclaimer_accepted_at,
    disclaimerScrollCompleted: row.disclaimer_scroll_completed,
    status: row.status as AmlDeclarationStatus,
    submittedAt: row.submitted_at,
    reviewedAt: row.reviewed_at,
    rejectionReason: row.rejection_reason,
    exportedToPartnerAt: row.exported_to_partner_at ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

function mapSexToGender(sex: string | null): AmlGender | null {
  if (sex === 'F' || sex === 'f' || sex === 'female') return 'female'
  if (sex === 'M' || sex === 'm' || sex === 'male') return 'male'
  return null
}

class AmlComplianceServiceImpl {
  async getDeclaration(profileId: string): Promise<AmlDeclaration | null> {
    const { data, error } = await supabase
      .from('aml_declarations')
      .select('*')
      .eq('profile_id', profileId)
      .maybeSingle()

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data) return null
    return mapRow(data)
  }

  /**
   * Everything Tago already knows about this person, with provenance.
   *
   * The verified identity document wins over the profile wherever both hold
   * the same datum: `identity_documents` was checked against the official
   * registry, the profile is self-typed (QUALITAS_MAPEO §3 B2). Nothing here
   * is ever asked again — the screen renders it as a confirmable summary.
   */
  async getIdentityPrefill(profile: UserProfile): Promise<AmlIdentityPrefill> {
    const sources: AmlIdentityPrefill['sources'] = {}

    let fullName: string | null = null
    let dateOfBirth: string | null = null
    let gender: AmlGender | null = null
    let nationalityCountryId: string | null = null

    // The verified identity comes from the one reader, not from a second copy
    // of this query. `identityService.getVerifiedIdentity` is what every other
    // screen uses; keeping a private query here is how this screen ended up
    // being the only one in the app that honoured "never ask twice".
    const verified = await identityService.getVerifiedIdentity(profile.id)

    if (verified) {
      if (verified.fullName) {
        fullName = verified.fullName
        sources.fullName = 'identity_document'
      }
      if (verified.dateOfBirth) {
        dateOfBirth = verified.dateOfBirth
        sources.dateOfBirth = 'identity_document'
      }

      gender = mapSexToGender(jsonString(jsonField(verified.extractedData ?? undefined, 'sex')))
      if (gender) sources.gender = 'identity_document'

      if (verified.issuingCountry) {
        nationalityCountryId = await this.resolveCountryId(verified.issuingCountry)
      }
      if (!nationalityCountryId) {
        const extractedNationality = jsonString(
          jsonField(verified.extractedData ?? undefined, 'nationality'),
        )
        if (extractedNationality) {
          nationalityCountryId = await this.resolveCountryId(extractedNationality)
        }
      }
      // A DUI is issued only to Salvadorans: the nationality is the document.
      if (!nationalityCountryId && verified.documentType === 'dui') {
        nationalityCountryId = await this.resolveCountryId(AML_SV_ISO2)
      }
      if (nationalityCountryId) sources.nationalityCountryId = 'identity_document'
    }

    if (!fullName) {
      const profileName =
        [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim() ||
        profile.displayName ||
        null
      if (profileName) {
        fullName = profileName
        sources.fullName = 'profile'
      }
    }

    if (!dateOfBirth && profile.dateOfBirth) {
      dateOfBirth = profile.dateOfBirth
      sources.dateOfBirth = 'profile'
    }

    if (profile.phone) sources.phone = 'profile'
    if (profile.countryId) sources.residenceCountryId = 'profile'

    return {
      fullName,
      phone: profile.phone,
      dateOfBirth,
      gender,
      nationalityCountryId,
      residenceCountryId: profile.countryId,
      sources,
    }
  }

  /** Build initial form values: existing declaration wins, else prefill. */
  async buildFormValues(profile: UserProfile): Promise<{
    values: AmlFormValues
    prefill: AmlIdentityPrefill
    declaration: AmlDeclaration | null
  }> {
    const [existing, prefill, alreadySignedDisclaimer] = await Promise.all([
      this.getDeclaration(profile.id),
      this.getIdentityPrefill(profile),
      this.hasCurrentDisclaimerAcceptance(profile.id),
    ])

    if (existing && existing.status !== 'draft') {
      return {
        declaration: existing,
        prefill,
        values: emptyAmlFormValues({
          fullName: existing.fullName,
          phone: existing.phone,
          dateOfBirth: existing.dateOfBirth,
          gender: existing.gender,
          nationalityCountryId: existing.nationalityCountryId,
          originCountryId: existing.originCountryId,
          residenceCountryId: existing.residenceCountryId,
          incomeRange: existing.incomeRange,
          economicActivity: toAmlEconomicActivity(existing.economicActivity),
          fundOrigin: existing.fundOrigin,
          isPep: existing.isPep,
          isPoliticalPartyMember: existing.isPoliticalPartyMember,
          hasPepFamily: existing.hasPepFamily,
          hasPepAssociate: existing.hasPepAssociate,
          hasTaxHavenRelations: existing.hasTaxHavenRelations,
          hasIllicitOrJudicialHistory: existing.hasIllicitOrJudicialHistory,
          disclaimerAccepted: !!existing.disclaimerAcceptedAt || alreadySignedDisclaimer,
          disclaimerScrollCompleted: existing.disclaimerScrollCompleted || alreadySignedDisclaimer,
        }),
      }
    }

    if (existing?.status === 'draft') {
      return {
        declaration: existing,
        prefill,
        values: emptyAmlFormValues({
          fullName: existing.fullName || prefill.fullName || '',
          phone: existing.phone || prefill.phone || '',
          dateOfBirth: existing.dateOfBirth || prefill.dateOfBirth || '',
          gender: existing.gender || prefill.gender,
          nationalityCountryId: existing.nationalityCountryId || prefill.nationalityCountryId,
          originCountryId: existing.originCountryId,
          residenceCountryId: existing.residenceCountryId || prefill.residenceCountryId,
          incomeRange: existing.incomeRange,
          economicActivity: toAmlEconomicActivity(existing.economicActivity),
          fundOrigin: existing.fundOrigin,
          isPep: existing.isPep,
          isPoliticalPartyMember: existing.isPoliticalPartyMember,
          hasPepFamily: existing.hasPepFamily,
          hasPepAssociate: existing.hasPepAssociate,
          hasTaxHavenRelations: existing.hasTaxHavenRelations,
          hasIllicitOrJudicialHistory: existing.hasIllicitOrJudicialHistory,
          disclaimerAccepted: alreadySignedDisclaimer,
          disclaimerScrollCompleted: alreadySignedDisclaimer,
        }),
      }
    }

    return {
      declaration: null,
      prefill,
      values: emptyAmlFormValues({
        fullName: prefill.fullName ?? '',
        phone: prefill.phone ?? '',
        dateOfBirth: prefill.dateOfBirth ?? '',
        gender: prefill.gender,
        nationalityCountryId: prefill.nationalityCountryId,
        residenceCountryId: prefill.residenceCountryId,
        disclaimerAccepted: alreadySignedDisclaimer,
        disclaimerScrollCompleted: alreadySignedDisclaimer,
      }),
    }
  }

  async submitDeclaration(profileId: string, form: AmlFormValues): Promise<AmlDeclaration> {
    const parsed = amlFormSchema.safeParse(form)
    if (!parsed.success) {
      const first = parsed.error.issues[0]
      throw ServiceError.validation(first?.message ?? 'aml.error_required', {
        errors: parsed.error.issues.map((i) => i.message),
        path: first?.path,
      })
    }

    const data = parsed.data
    await this.assertOriginCountryIfNeeded(data.nationalityCountryId, data.originCountryId)

    // Art. 44 D.L. 144/2024 — "en todo caso deberá mediar el consentimiento
    // previo del titular": the authorization is written BEFORE the declaration
    // exists, because it is the legal precondition for the transfer and not an
    // audit note about it. This is the opposite of a best-effort log: any
    // failure here throws and the declaration is never created, so we can
    // never hold a record destined for Mexico without its proof of consent.
    await this.recordCrossBorderTransferConsent(profileId)

    const now = new Date().toISOString()
    const payload: AmlInsert = {
      profile_id: profileId,
      full_name: data.fullName.trim(),
      phone: data.phone.trim(),
      date_of_birth: data.dateOfBirth,
      gender: data.gender as AmlInsert['gender'],
      nationality_country_id: data.nationalityCountryId,
      origin_country_id: data.originCountryId,
      residence_country_id: data.residenceCountryId,
      income_range: data.incomeRange as AmlInsert['income_range'],
      economic_activity: data.economicActivity,
      fund_origin: data.fundOrigin as AmlInsert['fund_origin'],
      is_pep: data.isPep,
      is_political_party_member: data.isPoliticalPartyMember,
      has_pep_family: data.hasPepFamily,
      has_pep_associate: data.hasPepAssociate,
      has_tax_haven_relations: data.hasTaxHavenRelations,
      has_illicit_or_judicial_history: data.hasIllicitOrJudicialHistory,
      disclaimer_version: QUALITAS_AML_DISCLAIMER_VERSION,
      disclaimer_accepted_at: now,
      disclaimer_scroll_completed: true,
      status: 'under_review',
      submitted_at: now,
      rejection_reason: null,
    }

    // Atomic merge on the profile-unique key: `upsert` replaces the old
    // read-then-write pair (two extra round trips on a chain this long, and a
    // merge race when a second device submits concurrently). `aml_declarations`
    // has `aml_declarations_profile_uidx` on profile_id, so this is a true
    // last-write-wins upsert. The row is returned so the mutation seeds the
    // declaration cache with what the server actually persisted.
    const { data: row, error } = await supabase
      .from('aml_declarations')
      .upsert(payload, { onConflict: 'profile_id' })
      .select('*')
      .single()
    if (error) throw ServiceError.fromSupabaseError(error)

    await this.recordDisclaimerArtifacts(profileId)
    return mapRow(row)
  }

  /**
   * The renter's express authorization to send this declaration abroad.
   *
   * Separate from the `third_party_sharing` consent below: that one covers the
   * insurer looking at the data under its own AML duty (legal obligation);
   * this one covers the data leaving El Salvador for the insurer's systems in
   * Mexico, which no law obliges and only the titular can authorize — hence
   * `legal_basis: 'consent'`. The stored form version identifies the exact
   * wording shown on screen, which is what Art. 54 makes us able to prove.
   *
   * Throws on failure by design; `submitDeclaration` does not catch it.
   */
  private async recordCrossBorderTransferConsent(profileId: string): Promise<void> {
    await complianceService.recordConsent(profileId, {
      consentType: 'cross_border_transfer',
      granted: true,
      purpose:
        `Transfer identity and AML declaration data to ${QUALITAS_AML_PARTNER.legalName}, ` +
        'including its systems located in Mexico, so the insurer can underwrite and issue ' +
        'the rental coverage',
      legalBasis: 'consent',
      consentFormVersion: AML_CROSS_BORDER_CONSENT_VERSION,
      consentFormUrl: AML_CROSS_BORDER_CONSENT_URL,
    })
  }

  /**
   * Immutable audit trail: legal_acceptances + third_party_sharing consent
   * for Qualitas list validation / anomaly reporting.
   */
  private async hasCurrentDisclaimerAcceptance(profileId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('legal_acceptances')
      .select('id')
      .eq('profile_id', profileId)
      .eq('document_type', QUALITAS_AML_DISCLAIMER_DOCUMENT_TYPE)
      .eq('document_version', QUALITAS_AML_DISCLAIMER_VERSION)
      .limit(1)
    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).length > 0
  }

  private async recordDisclaimerArtifacts(profileId: string): Promise<void> {
    const documentUrl = QUALITAS_AML_DISCLAIMER_URL
    const alreadySigned = await this.hasCurrentDisclaimerAcceptance(profileId)
    if (!alreadySigned) {
      const { error: legalError } = await supabase.from('legal_acceptances').insert({
        profile_id: profileId,
        document_type: QUALITAS_AML_DISCLAIMER_DOCUMENT_TYPE,
        document_version: QUALITAS_AML_DISCLAIMER_VERSION,
        document_url: documentUrl,
      })
      if (legalError) throw ServiceError.fromSupabaseError(legalError)
    }

    await complianceService.recordConsent(profileId, {
      consentType: 'third_party_sharing',
      granted: true,
      purpose: `Share identity and AML questionnaire data with ${QUALITAS_AML_PARTNER.legalName} for money-laundering and OFAC list validation`,
      legalBasis: 'legal_obligation',
      consentFormVersion: QUALITAS_AML_DISCLAIMER_VERSION,
      consentFormUrl: documentUrl,
    })
  }

  private async assertOriginCountryIfNeeded(
    nationalityCountryId: string,
    originCountryId: string | null,
  ): Promise<void> {
    const { data, error } = await supabase
      .from('countries')
      .select('id, code')
      .eq('id', nationalityCountryId)
      .maybeSingle()
    if (error) throw ServiceError.fromSupabaseError(error)
    const code = data?.code
    if (code && code.toUpperCase() !== AML_SV_ISO2 && !originCountryId) {
      throw ServiceError.validation('aml.error_origin_country')
    }
  }

  private async resolveCountryId(codeOrName: string): Promise<string | null> {
    const raw = codeOrName.trim()
    if (!raw) return null

    if (raw.length === 2) {
      const { data } = await supabase
        .from('countries')
        .select('id')
        .eq('code', raw.toUpperCase())
        .eq('is_active', true)
        .maybeSingle()
      return data?.id ?? null
    }

    const normalized = raw.toLowerCase()
    if (
      normalized.includes('salvador') ||
      normalized === 'sv' ||
      normalized.includes('salvadore')
    ) {
      const { data } = await supabase
        .from('countries')
        .select('id')
        .eq('code', AML_SV_ISO2)
        .maybeSingle()
      return data?.id ?? null
    }

    const { data } = await supabase
      .from('countries')
      .select('id')
      .ilike('name', raw)
      .eq('is_active', true)
      .limit(1)
      .maybeSingle()
    return data?.id ?? null
  }
}

export const amlComplianceService = new AmlComplianceServiceImpl()
